<script lang="ts" setup>
import { DateTime } from 'luxon';
import EditorModal from './partials/EditorModal.vue';
import { editorAPI } from '#layers/core/app/components/edito/editor/api';

const model = defineModel<string>({ default: '' });
const props = withDefaults(defineProps<{
    enabled?: boolean;
}>(), {
    enabled: false
});

const content = computed({
    get() { return model.value; },
    set(value) { model.value = value; }
});

// Timeline
const timelineModal = ref<any>(null);
const timelineSelected = ref();
const timelineData = ref([
    {
        id: 0,
        datetime: DateTime.now().setZone('Europe/Warsaw').toFormat('yyyy-MM-dd\'T\'HH:mm'),
        title: '',
        content: ''
    }
]);
const sortedTimeline = computed(() => [...timelineData.value].sort((a, b) =>
{
    const index = (new Date(b.datetime).getTime() - new Date(a.datetime).getTime()) || 0;

    return index;
}));
const timelineActiveIndex = ref(0);
const timelineIndex = computed(() => timelineData.value.findIndex(el => el.id === timelineActiveIndex.value) ?? 0);
const resetTimeline = (): void =>
{
    timelineData.value = [
        {
            id: 0,
            datetime: DateTime.now().setZone('Europe/Warsaw').toFormat('yyyy-MM-dd\'T\'HH:mm'),
            title: '',
            content: ''
        }
    ];
};

const addNewTimelineItem = (): void =>
{
    const id: number = timelineData.value.reduce((prev, curr) => prev.id > curr.id ? prev : curr).id + 1;

    timelineData.value.push({
        id,
        datetime: DateTime.now().setZone('Europe/Warsaw').toFormat('yyyy-MM-dd\'T\'HH:mm'),
        title: '',
        content: ''
    });
    timelineActiveIndex.value = id;
};
const removeTimelineItem = (): void =>
{
    const index = timelineData.value.findIndex(el => el.id === timelineActiveIndex.value);

    if (index > -1)
    {
        timelineData.value.splice(index, 1);

        const id = timelineData.value.length === 0 || index === 0 ? 0 : index - 1;

        timelineActiveIndex.value = timelineData.value[id].id;
    }
};
const handleSubmitTimeline = (type?: 'remove'): void =>
{
    let items = '';

    sortedTimeline.value.forEach((el) =>
    {
        items += `
            <div class="mce-timeline-item" data-item-id="${el.id}">
                <div class="mce-timeline-date">${el.datetime.replace('T', ' ')}</div>
                <div class="mce-timeline-title">${el.title}</div>
                <div class="mce-timeline-content">${el.content}</div>
            </div>
        `;
    });

    nextTick(() =>
    {
        const timestamp = new Date().getTime();

        const parser = new DOMParser();
        const doc = parser.parseFromString(content.value, 'text/html');
        const tm = doc.querySelector(`.mce-timeline[data-timestamp="${timelineSelected.value}"]`);

        const htmlToAdd = `
                <div class="mceNonEditable mce-timeline" data-timestamp="${timestamp}">
                    ${items}
                </div>
            `;

        if (timelineSelected.value && tm)
        {
            // Replace exist timeline
            if (type !== 'remove')
                tm.insertAdjacentHTML('beforebegin', htmlToAdd);

            tm.remove();

            content.value = doc.body.innerHTML;
        }
        else
        {
            // Add new timeline
            editorAPI().activeEditor.execCommand('mceInsertContent', false, htmlToAdd);
        }

        timelineModal.value.hide();

        resetTimeline();
    });
};

function show()
{
    resetTimeline();
    timelineActiveIndex.value = 0;
    timelineSelected.value = null;
    timelineModal.value.show();
}

function edit(timestamp: any, html: string)
{
    timelineSelected.value = timestamp;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const items = doc.querySelectorAll('.mce-timeline-item');

    timelineData.value = [];

    items.forEach((item: any) =>
    {
        timelineData.value.push({
            id: parseInt(item.dataset.itemId),
            datetime: item.querySelector('.mce-timeline-date').textContent,
            title: item.querySelector('.mce-timeline-title').textContent,
            content: item.querySelector('.mce-timeline-content').innerHTML
        });
    });

    if (timelineData.value.findIndex(el => el.id === timelineActiveIndex.value) === -1)
        timelineActiveIndex.value = timelineData.value[0].id;

    timelineModal.value.show();
}

defineExpose({
    init(editor: any)
    {
        editor.ui.registry.addMenuItem('timeline', {
            icon: 'insert-time',
            text: $t('[[[Oś czasu]]]'),
            onAction: () =>
            {
                show();
            }
        });

        editor.on('ObjectSelected', function (e: any): void
        {
            // timeline clicked
            if (e.target.classList.contains('mce-timeline'))
            {
                edit(e.target.dataset.timestamp, e.target.innerHTML);
            }
        });
    }
});
</script>

<template>
    <EditorModal
        v-if="props.enabled"
        ref="timelineModal"
        v-model:active-tab="timelineActiveIndex"
        :tabs="sortedTimeline"
        :title="$t('[[[Chronologia zdarzeń]]]')"
        size="lg"
        @ok="handleSubmitTimeline"
    >
        <template #prepend-footer>
            <button
                :title="$t('[[[Dodaj kolejny element]]]')"
                type="button"
                tabindex="-1"
                data-alloy-tabstop="true"
                class="tox-button tox-button--secondary"
                @click="addNewTimelineItem"
            >
                {{ $t('[[[Dodaj kolejny element]]]') }}
            </button>

            <button
                v-if="timelineData.length > 1"
                :title="$t('[[[Usuń wybrany element]]]')"
                type="button"
                tabindex="-1"
                data-alloy-tabstop="true"
                class="tox-button tox-button--secondary"
                @click="removeTimelineItem"
            >
                {{ $t('[[[Usuń wybrany element]]]') }}
            </button>
        </template>

        <template #append-footer>
            <button
                v-if="timelineSelected"
                :title="$t('[[[Usuń charmonogram]]]')"
                type="button"
                tabindex="-1"
                data-alloy-tabstop="true"
                class="tox-button tox-button--secondary"
                @click="handleSubmitTimeline('remove')"
            >
                {{ $t('[[[Usuń]]]') }}
            </button>
        </template>

        <div class="tox-form__group" aria-disabled="false">
            <label class="tox-label" for="">{{ $t('[[[Data i godzina]]]') }}</label>
            <input v-model="timelineData[timelineIndex].datetime" type="datetime-local" tabindex="-1" data-alloy-tabstop="true" class="tox-textfield">
        </div>
        <div class="tox-form__group" aria-disabled="false">
            <label class="tox-label" for="">{{ $t('[[[Tytuł elementu]]]') }}</label>
            <input v-model="timelineData[timelineIndex].title" type="text" tabindex="-1" data-alloy-tabstop="true" class="tox-textfield">
        </div>
        <div class="tox-form__group" aria-disabled="false">
            <label class="tox-label" for="">{{ $t('[[[Treść]]]') }}</label>
            <RichTextEditor id="timeline-editor" v-model="timelineData[timelineIndex].content" />
        </div>
    </EditorModal>
</template>
